/**
 * AdminAssignments — /admin/assignments
 *
 * Cross-course gradebook for the light assignment flow. Lists every
 * submission (newest first), with the assignment + lesson title, the
 * student, their submitted body / files, and inline Pass / Fail /
 * Needs revision buttons + a short feedback note field. Persists via
 * `PUT /api/lms/submissions/{id}/grade`.
 *
 * Status legend:
 *   submitted → not yet reviewed (default filter shows only these)
 *   graded    → decision recorded
 *   returned  → sent back for revision (student sees "needs revision")
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import {
  CheckCircle2, XCircle, RotateCcw, ClipboardList, ExternalLink, Filter, RefreshCw,
} from 'lucide-react';

// Score conventions: 100 = pass, 0 = fail. We keep the numeric column
// so a future rubric UI can plug in without a schema change.
const DECISIONS = {
  pass:    { label: 'Pass',            score: 100, status: 'graded',   icon: CheckCircle2, tone: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30' },
  fail:    { label: 'Fail',            score: 0,   status: 'graded',   icon: XCircle,      tone: 'bg-rose-500/10 text-rose-700 border-rose-500/30' },
  revise:  { label: 'Needs revision',  score: 50,  status: 'returned', icon: RotateCcw,    tone: 'bg-amber-500/10 text-amber-700 border-amber-500/30' },
};

const STATUS_TONE = {
  submitted: 'bg-sky-500/15 text-sky-700 border-sky-500/30',
  graded:    'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  returned:  'bg-amber-500/15 text-amber-700 border-amber-500/30',
};

export default function AdminAssignments() {
  const { API } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('submitted'); // submitted | graded | returned | all
  const [feedback, setFeedback] = useState({}); // { [subId]: 'note...' }
  const [grading, setGrading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const q = filter === 'all' ? '' : `?status=${filter}`;
      const { data } = await axios.get(`${API}/admin/lms/submissions${q}`);
      setRows(data.rows || []);
    } catch (_) {
      toast.error('Could not load submissions');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const decide = async (sub, decisionKey) => {
    const d = DECISIONS[decisionKey];
    if (!d) return;
    try {
      setGrading(sub.id);
      await axios.put(`${API}/lms/submissions/${sub.id}/grade`, {
        score: d.score,
        feedback: feedback[sub.id] || null,
        status: d.status,
      });
      toast.success(`${d.label} recorded`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Grade failed');
    } finally { setGrading(null); }
  };

  const counts = useMemo(() => ({
    total: rows.length,
    submitted: rows.filter((r) => r.status === 'submitted').length,
    graded: rows.filter((r) => r.status === 'graded').length,
    returned: rows.filter((r) => r.status === 'returned').length,
  }), [rows]);

  return (
    <div className="container mx-auto px-4 sm:px-6 py-8 max-w-5xl" data-testid="admin-assignments-page">
      <div className="flex items-start gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
          <ClipboardList className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Grading</div>
          <h1 className="font-heading text-2xl font-extrabold text-foreground">Assignment submissions</h1>
          <p className="text-sm text-muted-foreground">
            Review student work across every lesson and issue Pass / Fail / Needs revision decisions.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="rounded-full" data-testid="admin-assignments-refresh">
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <Filter className="w-3.5 h-3.5 text-muted-foreground" />
        {[
          { k: 'submitted', l: `Awaiting review · ${counts.submitted}` },
          { k: 'returned',  l: `Sent back · ${counts.returned}` },
          { k: 'graded',    l: `Graded · ${counts.graded}` },
          { k: 'all',       l: 'All' },
        ].map((f) => (
          <button
            key={f.k}
            type="button"
            onClick={() => setFilter(f.k)}
            className={`rounded-full px-3 h-8 text-xs font-bold uppercase tracking-widest border transition-colors ${filter === f.k ? 'bg-foreground text-background border-foreground' : 'text-muted-foreground border-border hover:text-foreground'}`}
            data-testid={`admin-assignments-filter-${f.k}`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {loading && <div className="text-center py-16 text-sm text-muted-foreground">Loading submissions…</div>}

      {!loading && rows.length === 0 && (
        <div className="text-center py-16 border border-dashed border-border rounded-2xl text-sm text-muted-foreground" data-testid="admin-assignments-empty">
          Nothing to review here.
        </div>
      )}

      <ul className="space-y-3">
        {rows.map((r) => {
          const files = r.file_urls && r.file_urls.length ? r.file_urls : (r.file_url ? [r.file_url] : []);
          return (
            <li key={r.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`admin-assignment-${r.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest font-black rounded-full px-2 py-0.5 border ${STATUS_TONE[r.status] || 'border-border'}`}>
                      {r.status}
                    </span>
                    <span className="font-heading font-extrabold text-base truncate">
                      {r.assignment_title || 'Untitled assignment'}
                    </span>
                    {r.lesson_title && (
                      <span className="text-[11px] text-muted-foreground truncate">· {r.lesson_title}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {r.user_email} · submitted {new Date(r.submitted_at).toLocaleString()}
                    {r.graded_by && <> · graded by {r.graded_by}</>}
                  </div>
                </div>
              </div>

              {r.content && (
                <div className="mt-3 rounded-lg border border-border bg-muted/30 p-3 text-sm whitespace-pre-wrap" data-testid={`admin-assignment-body-${r.id}`}>
                  {r.content}
                </div>
              )}

              {(files.length > 0 || r.external_url) && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {files.map((u, i) => (
                    <a
                      key={i}
                      href={u}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline"
                      data-testid={`admin-assignment-file-${r.id}-${i}`}
                    >
                      <ExternalLink className="w-3 h-3" /> File {i + 1}
                    </a>
                  ))}
                  {r.external_url && (
                    <a
                      href={r.external_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline"
                      data-testid={`admin-assignment-external-${r.id}`}
                    >
                      <ExternalLink className="w-3 h-3" /> External link
                    </a>
                  )}
                </div>
              )}

              {r.feedback && (
                <div className="mt-2 text-[11px] italic text-muted-foreground">
                  Prior feedback: {r.feedback}
                </div>
              )}

              <div className="mt-3 flex items-start gap-2 flex-wrap">
                <textarea
                  rows={2}
                  placeholder="Feedback for the student (optional)"
                  value={feedback[r.id] || ''}
                  onChange={(e) => setFeedback((f) => ({ ...f, [r.id]: e.target.value }))}
                  className="flex-1 min-w-[200px] rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  data-testid={`admin-assignment-feedback-${r.id}`}
                />
                <div className="flex items-center gap-1.5 flex-wrap">
                  {Object.entries(DECISIONS).map(([key, d]) => {
                    const Icon = d.icon;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => decide(r, key)}
                        disabled={grading === r.id}
                        className={`inline-flex items-center gap-1 rounded-full px-3 h-8 text-xs font-bold border ${d.tone} hover:brightness-95 disabled:opacity-50 transition-colors`}
                        data-testid={`admin-assignment-${key}-${r.id}`}
                      >
                        <Icon className="w-3.5 h-3.5" /> {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
